import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, ApiError, slotCard } from '../api.js';
import type { ChatMessage, CourseLayout, GameSettings as Settings, GameView, OptionCardView, PlayerRow, ProgramCard, RelativeFacing, RobotView, RegisterSlot } from '../api.js';
import { Board } from '../components/Board.js';
import { BoardKey } from '../components/BoardKey.js';
import { GameSettings } from '../components/GameSettings.js';
import { Programmer } from '../components/Programmer.js';
import { PowerDownChoice } from '../components/PowerDownChoice.js';
import { CARD_LABEL } from '../cardLabels.js';
import type { Award } from '../components/Programmer.js';
import { LogPanel } from '../components/LogPanel.js';
import { ChatPanel } from '../components/ChatPanel.js';
import { Countdown } from '../components/Countdown.js';
import { OptionTray } from '../components/OptionTray.js';
import { RobotStats } from '../components/RobotStats.js';
import { archiveSquare, useSteppedPlayback } from '../components/useSteppedPlayback.js';
import { useGameLog } from '../components/useGameLog.js';
import { useTurnBeep } from '../components/useTurnBeep.js';
import { useReplay } from '../components/useReplay.js';
import { useCurrentGame } from '../App.js';
import { colourMap, nameMap } from '../identity.js';

/**
 * The board screen. One view, driven by whatever phase the engine reports:
 *
 *   awaitingProgram        hand and five registers
 *   awaitingPowerDown      power down for next turn, yes or no
 *   runningRegister        playback, advanced a register at a time by
 *                          the player, deliberately not on a timer
 *   awaitingEndOfTurn      facing, repair, continue power down
 *   gameOver               who won
 *
 * The client never computes game state. It draws what the server sends and
 * sends back intent. Every position on screen came from the engine.
 */

export function Game() {
  const { gameId } = useParams();
  const id = Number(gameId);
  const navigate = useNavigate();

  const [view, setView] = useState<GameView | null>(null);
  const [layout, setLayout] = useState<CourseLayout | null>(null);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [players, setPlayers] = useState<PlayerRow[]>([]);
  const [chat, setChat] = useState<ChatMessage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Programming is local until submitted, so a half-built program is not
  // sent to the server on every drag.
  const [draft, setDraft] = useState<(ProgramCard | null)[]>([null, null, null, null, null]);
  const [submitted, setSubmitted] = useState(false);
  const [powerDown, setPowerDown] = useState(false);
  const [startFacing, setStartFacing] = useState<'N' | 'E' | 'S' | 'W'>('N');

  // Register playback. `shown` is the register the player is looking at,
  // which lags the server when several resolve at once.
  const [shown, setShown] = useState(0);

  const cellPx = 44;

  const load = useCallback(async () => {
    try {
      const next = await api.gameView(id);
      setView(next);
      return next;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the server.');
      return null;
    }
  }, [id]);

  useEffect(() => {
    void (async () => {
      const [v, l, g] = await Promise.allSettled([
        api.gameView(id), api.courseLayout(id), api.gameSettings(id),
      ]);
      if (v.status === 'fulfilled') setView(v.value);
      if (l.status === 'fulfilled') setLayout(l.value);
      // Nothing to show if it fails: the table is a reference, not play.
      if (g.status === 'fulfilled') setSettings(g.value);
      if (v.status === 'rejected') {
        setError(v.reason instanceof ApiError ? v.reason.message : 'Could not load the game.');
      }
    })();
  }, [id]);

  /**
   * Bumped to throw away a stream that has stopped saying anything.
   *
   * A dropped connection EventSource reopens by itself. What it cannot
   * notice is a connection that stays open and delivers nothing -- a
   * proxy holding the response, a half-open socket after a sleep or a
   * change of network -- and the page then shows the last state it heard
   * about for as long as it is left open. That is a frozen game as far as
   * the player is concerned, and reloading lands in the same place.
   */
  const [streamEpoch, setStreamEpoch] = useState(0);

  // The roster is re-read whenever the robots in the state change: a late
  // joiner's robot arrives at the Deal, and a removal eliminates one. Keyed
  // on that rather than on every state, which would re-read it on every
  // advance for nothing.
  const rosterKey = view?.robots.map((r) => `${r.id}:${r.eliminated ? 1 : 0}`).join(',') ?? '';
  useEffect(() => {
    if (!rosterKey) return;
    api.players(id).then((r) => setPlayers(r.players)).catch(() => undefined);
  }, [id, rosterKey]);

  // Read whole on opening and on every fresh stream, since a line said
  // while the stream was down never arrives on it.
  useEffect(() => {
    api.chat(id).then((r) => setChat(r.messages)).catch(() => undefined);
  }, [id, streamEpoch]);

  /** A line off the stream or from our own send, whichever lands first. */
  const addChat = useCallback((message: ChatMessage) => {
    setChat((prev) => (prev.some((m) => m.id === message.id) ? prev : [...prev, message]));
  }, []);

  // Live updates. The stream sends a complete redacted view on every
  // change, so there is no incremental state to keep in sync.
  useEffect(() => {
    const source = new EventSource(`/api/games/${id}/stream`, { withCredentials: true });
    // When the stream was last heard from, event or heartbeat. The server
    // pings every 20 seconds, so silence for much longer than that is a
    // stream that is no longer carrying anything.
    let heard = Date.now();
    source.addEventListener('state', (event) => {
      heard = Date.now();
      setView(JSON.parse((event as MessageEvent).data) as GameView);
    });
    source.addEventListener('chat', (event) => {
      heard = Date.now();
      addChat(JSON.parse((event as MessageEvent).data) as ChatMessage);
    });
    source.addEventListener('ping', () => { heard = Date.now(); });
    // EventSource reconnects on its own; a one-off failure is not worth
    // showing the player.
    source.onerror = () => undefined;

    const watchdog = setInterval(() => {
      if (Date.now() - heard < 55000) return;
      // Two and a half missed heartbeats. Take the stream down, refetch
      // the state it may have missed, and open a new one -- rather than
      // leaving the player looking at a board that has stopped moving
      // with nothing to tell them so.
      source.close();
      void load();
      setStreamEpoch((n) => n + 1);
    }, 10000);

    return () => {
      clearInterval(watchdog);
      source.close();
    };
    // `load` is stable for a given game; `streamEpoch` is what reopens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, streamEpoch]);

  /** A card placed on an Option that holds one, and gadgets programmed
   * into registers. Both travel with the program submission. */
  // A list: a robot holding both a Conditional Program and a Flywheel can
  // feed both in the same turn, and a single slot made them exclusive.
  const [setAside, setSetAside] =
    useState<{ optionId: string; card: ProgramCard }[]>([]);
  const [gadgets, setGadgets] = useState<string[][]>([[], [], [], [], []]);
  /** Turn-programmed Options switched on for this turn, by Option id. */
  const [activated, setActivated] = useState<string[]>([]);
  /** Which way each aimed Option is turned for this turn. One left out
   * faces front, so an untouched card sends nothing. */
  const [aimed, setAimed] =
    useState<{ optionId: string; facing: RelativeFacing }[]>([]);
  /** What to do at a chop shop this register, if standing on one. */
  const [chopChoice, setChopChoice] =
    useState<{ action: 'reload' | 'exchange' | 'draw' | 'nothing'; optionId?: string } | null>(null);

  const { setGame } = useCurrentGame();
  const debrisOn = view?.debris;

  // A new turn clears everything programmed for the last one — the cards,
  // the gadgets loaded into registers, and any card set aside. Leaving the
  // gadgets meant the same dropper was resubmitted every turn until its
  // payload ran out.
  const turn = view?.turnNumber;
  useEffect(() => {
    setDraft([null, null, null, null, null]);
    setSubmitted(false);
    setPowerDown(false);
    setGadgets([[], [], [], [], []]);
    // Turn-programmed activations are per turn on the engine side too, so
    // leaving them set would silently re-activate the same card every
    // turn -- the same trap the gadget list had.
    setActivated([]);
    // "On each turn, you must program the direction" -- the engine clears
    // the aim at End of Turn, so a card left turned here would show a side
    // the robot does not have.
    setAimed([]);
    setSetAside([]);
    setChopChoice(null);
  }, [turn]);

  // A chop shop pick belongs to the REGISTER it was made for, and was
  // only ever cleared with the turn. Left standing, the pick made at one
  // register was still in state at the next: the panel opened with it
  // already selected, and -- for the host, whose pick travels with the
  // advance rather than being parked -- it was SENT again. A robot that
  // stayed on the shop scrapped a second Option for a choice made once.
  // Each register asks afresh, so the answer must not outlive it.
  const choiceRegister = view?.phase.kind === 'awaitingRegisterChoices'
    ? view.phase.register : null;
  useEffect(() => {
    setChopChoice(null);
  }, [choiceRegister]);

  // Every phase that BELONGS to a register in progress.
  //
  // The three mid-register prompts were missing, and the playback's
  // `!enabled` branch passes over unplayed entries rather than queueing
  // them -- so a register that stopped to ask about an option, a weapon
  // or an Abort Switch threw away everything logged up to that point.
  // The board then drew from server state, which already holds the end of
  // the move: the net result was right and none of it played out, so a
  // push looked like both robots arriving at once.
  //
  // Same three phases that were missing from `revealedThrough` in the
  // server's view. Worth checking any other list that names phases.
  const inPlayback = view?.phase.kind === 'runningRegister'
    || view?.phase.kind === 'awaitingRegisterChoices'
    || view?.phase.kind === 'awaitingOptionUse'
    || view?.phase.kind === 'awaitingWeaponChoice'
    || view?.phase.kind === 'awaitingAbortChoice';
  // One fetch of the log per state change, shared by the playback and the
  // panel rather than each requesting it separately.
  const log = useGameLog(id, view?.version ?? 0);

  const {
    positions, playing, wreckage, tokens: heldTokens, flags: heldFlags,
    archives: heldArchives, shot: heldShot,
  } = useSteppedPlayback(
    view, Boolean(inPlayback), log.entries, log.loaded,
  );
  const replay = useReplay();

  // A replay overrides everything else on the board. It is explicitly
  // asked for, so it wins over both the live playback and the current
  // positions until it finishes.
  const boardPositions = replay.positions ?? positions;

  // While a register is playing back, robots are drawn where the replay
  // has reached. Once it finishes, `positions` is null and the server's
  // own positions are shown, so the board can never drift from the truth.
  // Tokens are held back while a register plays out, for the same reason
  // robot positions are. The hold is owned by the playback hook rather
  // than worked out here: it has to be taken the moment the new state
  // arrives, which is BEFORE `playing` turns true, so anything keyed on
  // `playing` snapshots the new tokens and holds those -- no hold at all,
  // which is why the debris still appeared ahead of the fall.
  // A replay's own layer wins: reviewing turn 3 at turn 12 must show the
  // board as it was, not today's debris under an old register. Null both
  // when nothing is being replayed and when the frame predates the layer
  // being recorded, and the live board is the best answer to either.
  const shownTokens = replay.tokens ?? heldTokens ?? view?.tokens ?? [];

  // The flags as the playback has them, so one riding a belt moves when
  // the belts run rather than the moment the register resolves.
  //
  // A replay's own win, as its tokens do: reviewing turn 3 at turn 12 must
  // show the flags where they stood then. Undefined where nothing is being
  // replayed and on a course whose flags have never moved, and the board
  // draws the course's own for both.
  const shownFlags = replay.courseFlags ?? heldFlags ?? view?.flags;

  // The shot to draw. A replay's own wins the way its tokens do, and
  // there is no third source: the live view says where robots ended the
  // register, never what was fired during it.
  const shownShot = replay.replaying ? replay.shot : heldShot;

  // And where each archive marker stands. A marker is taken at step E, so
  // through a register it belongs where the last one left it -- not on the
  // flag or the wrench site this register is about to hand out.
  const shownArchives = replay.archives ?? heldArchives;

  // A wreck belongs to the register the LIVE game just played. While an
  // earlier one is being replayed it is somebody else's, and striking a
  // robot through in a register it came out of unharmed says something
  // about that register that is not true.
  const shownWrecks = replay.replaying
    ? new Set<string>()
    : new Set(wreckage.keys());

  /**
   * What this robot won at one point in the game, read off the log.
   *
   * `registers` names the register whose Touch Checkpoints produced it: a
   * chop shop and radioactive waste both hand cards over there, and each
   * register's advance is logged against its own number. `null` is the
   * end of turn, which has no register and is where a repair site's
   * Option comes from.
   *
   * A register's result belongs to that register. Every award used to be
   * held back and shown in one block on the next turn's programming
   * screen, so a swap at register 4 and the free Option at register 5
   * arrived together, looking like one answer had been acted on twice.
   */
  const awardsFrom = useCallback((
    turn: number | null | undefined,
    registers: (number | null)[],
  ): Award[] => {
    const meId = view?.youAre;
    if (!turn || !meId) return [];
    const found: Award[] = [];
    for (const entry of log.entries) {
      if (entry.turn !== turn) continue;
      if (!registers.includes(entry.register)) continue;
      for (const e of entry.events) {
        // Radioactive waste hands over a card exactly as a repair site
        // does, so it announces the same way. It was missing here, which
        // is why an Option won in the waste simply appeared: the log said
        // the robot drew, and nothing ever named the card.
        const kind: Award['kind'] | null =
          e.type === 'optionExchanged' ? 'swapped'
            : e.type === 'ammoReloaded' ? 'reloaded'
              : (e.type === 'optionGranted' || e.type === 'radioactiveWasteDrawn')
                ? 'taken' : null;
        if (!kind) continue;
        if (e.robotId !== meId || !e.name) continue;
        if (found.some((f) => f.name === e.name && f.kind === kind)) continue;
        found.push({ name: e.name, kind, gaveUp: e.gaveUp });
      }
    }
    return found;
  }, [log.entries, view?.youAre]);

  /**
   * The programming screen's window: register 5 and the end of turn, of
   * the turn just played. Registers 1 to 4 have each already been
   * reported at the pause that follows them; register 5's checkpoints
   * resolve into the end of turn, which does not stop unless somebody
   * owes a decision, so this is the first place its result can be shown.
   */
  const gainedOptions = useMemo(
    () => awardsFrom(view?.turnNumber ? view.turnNumber - 1 : null, [5, null]),
    [awardsFrom, view?.turnNumber],
  );

  const shownRobots = view
    ? view.robots.map((r) => {
      // Wrecked in the register just played: left where it died, marked
      // with a cross, until the next register starts. Takes precedence
      // over everything else, including the server having already
      // respawned it.
      if (replay.positions) {
        const at = replay.positions.get(r.id);
        // A robot absent from the replay frame was not on the board then.
        if (!at) return { ...r, destroyed: true };
        const was = replay.flags?.get(r.id);
        return {
          ...r, x: at.x, y: at.y, facing: at.facing,
          damage: at.damage, destroyed: false,
          // As the frame had them, so an old register is not drawn with
          // today's power-downs and archives over it. `flags` is null on
          // a frame written before they were recorded, and the live
          // values stand for those -- the best answer available, and
          // usually right for a register from this turn or the last.
          ...(replay.flags ? {
            eliminated: was?.eliminated ?? false,
            virtual: was?.virtual ?? false,
            poweredDown: was?.poweredDown ?? false,
            archiveMarker: was?.archiveMarker,
            archiveFlag: was?.archiveFlag,
          } : {}),
        };
      }
      // A wreck belongs to the register being played back and to nothing
      // else. Left ungated it survived End of Turn and into the next
      // turn's programming, so the board went on showing a dead robot in
      // the square it died in while the one that had come back at its
      // archive was not drawn at all.
      const wreck = inPlayback ? wreckage.get(r.id) : undefined;
      if (wreck) {
        return {
          ...r, x: wreck.x, y: wreck.y, facing: wreck.facing,
          damage: wreck.damage, destroyed: false,
        };
      }
      if (!boardPositions) return r;
      const at = boardPositions.get(r.id);
      // Present in the replay: draw it where the replay has it, and treat
      // it as alive even if the server has already destroyed it, so the
      // player sees it drive off the edge rather than simply vanish.
      if (at) {
        // Damage as of this point in the replay, not the register's final
        // total: a laser hit must show when the beam fires.
        return {
          ...r, x: at.x, y: at.y, facing: at.facing,
          damage: at.damage, destroyed: false,
        };
      }
      return { ...r, destroyed: true };
    })
      // The archive is the flag, so a marker set on one is drawn wherever
      // the flags being drawn have it -- which keeps it with the flag
      // through a playback instead of jumping to where the register left
      // it while the flag is still being carried there.
      .map((r) => {
        const square = archiveSquare(shownArchives?.get(r.id) ?? r, shownFlags);
        return square === r.archiveMarker ? r : { ...r, archiveMarker: square };
      })
    : [];

  const me = view?.robots.find((r) => r.id === view.youAre) ?? null;

  /**
   * Whether this player is being asked which way their robot faces.
   *
   * TURN 1 FOR THIS PLAYER, which is not always turn 1 of the game: a
   * robot that joined part way through is standing on its square pointing
   * north with no more say in it than anybody had at the start.
   *
   * One value for all four places that need it -- the board preview, the
   * picker, the draft and the submission -- because they were three
   * separate copies of `turnNumber === 1` and a joiner got the picker
   * from one of them and nothing from the other two, so the choice was
   * offered and then thrown away.
   */
  const picksStartFacing = Boolean(
    view && view.phase.kind === 'awaitingProgram'
      && (view.turnNumber === 1 || me?.joinedTurn === view.turnNumber),
  );

  /**
   * The start facing chosen on the programming screen, drawn before it is
   * sent.
   *
   * The answer only reaches the server on submit -- so the board went on
   * drawing the robot the way the server still had it while the player
   * clicked through the four buttons, which is the one moment the picture
   * is meant to answer the question being asked.
   */
  const drawnRobots = picksStartFacing
    ? shownRobots.map((r) => (
      r.id === view?.youAre ? { ...r, facing: startFacing } : r
    ))
    : shownRobots;

  /**
   * Whether a host override actually has SOMEONE ELSE to act for.
   *
   * Every waiting phase carries `robotIds`, and it shrinks as answers come
   * in — so if the only robot still outstanding is your own, the override
   * has nothing to do but answer on your own behalf: fill your own
   * registers at random from your own hand, record your own power-down as
   * no, decline your own option. That is not what any of the three
   * buttons is for, and offering it invites a misclick that costs the
   * host their own turn.
   *
   * This is most obvious in a solo game, where the button was showing
   * every single turn, but it is the same wart in a full game where
   * everyone else has already answered and you are the last one left.
   *
   * If `me` is ever null the override stays available, since every
   * outstanding robot is then somebody else. That branch is defensive
   * rather than live: `isHost` is derived by matching `view.youAre` to a
   * player row, so a host without a robot of their own does not read as
   * host in the first place and sees none of these buttons anyway.
   */
  const waitingOnSomeoneElse = (robotIds: string[]) =>
    robotIds.some((rid) => rid !== me?.id);

  // The host advances the turn; everyone else submits their choice and
  // waits. `created_by` rides along on every player row.
  const myUserId = players.find((p) => p.robot_id === view?.youAre)?.user_id;
  const isHost = myUserId !== undefined
    && players[0]?.created_by !== null
    && players[0]?.created_by === myUserId;

  const names = useMemo(() => nameMap(players), [players]);

  const nameOf = useCallback(
    (robotId: string) => names.get(robotId) ?? robotId,
    [names],
  );

  /** Everyone named, for a line of text: "Ann", "Ann and Bob",
   * "Ann, Bob and Cy". */
  const namesOf = (robotIds: string[]): string => {
    const all = robotIds.map(nameOf);
    return all.length > 1
      ? `${all.slice(0, -1).join(', ')} and ${all[all.length - 1]}`
      : all[0] ?? 'other players';
  };

  const colours = useMemo(
    () => colourMap(players, view?.robots ?? []),
    [players, view],
  );

  // Tell the shell which game is on screen: the rules summary in the
  // masthead hides the notes that do not apply to it, and the masthead
  // lists the table in seat order, each name in its robot's colour.
  // Cleared on the way out: the lobby must not still be answering for the
  // last game played.
  const table = useMemo(
    () => players.map((p) => ({
      name: p.display_name,
      colour: colours.get(p.robot_id) ?? 'var(--paint-blue)',
    })),
    [players, colours],
  );
  useEffect(() => {
    if (debrisOn === undefined) return undefined;
    setGame({ debris: debrisOn, players: table });
    return () => setGame(null);
  }, [debrisOn, table, setGame]);

  // Locked registers already hold their card; seed the draft with them so
  // the submission is complete without the player touching them.
  const locked = me?.lockedRegisters ?? [false, false, false, false, false];
  // For DISPLAY: a locked register shows the card it already holds, so
  // the player can see their whole program.
  const workingDraft = useMemo(() => {
    if (!me) return draft;
    return draft.map((card, i) => (locked[i] ? slotCard(me.registers[i]) : card));
  }, [draft, me, locked]);

  // For SUBMISSION: a locked register must be null. It already holds the
  // card it will execute again, and the engine rejects any attempt to
  // program one — which silently failed every submission from a damaged
  // robot until this was separated from the display version.

  const submittedDraft = useMemo(
    () => workingDraft.map((card, i) => (locked[i] ? null : card)),
    [workingDraft, locked],
  );

  // A gadget can only be programmed if the robot still holds the Option.
  // The raw `gadgets` state is not enough on its own: being destroyed
  // costs the player an Option, and an entry naming one they no longer
  // own is both unsubmittable (the engine rejects "no option X to
  // program") and undeselectable, because the row of slot buttons that
  // would clear it is only rendered for Options the robot holds. The
  // player was left with a program that could not be sent and no way to
  // repair it. Deriving the list means a lost Option simply disappears
  // from the registers.
  const heldOptionIds = useMemo(
    () => new Set((me?.options ?? []).map((o) => o.id)),
    [me],
  );
  const liveGadgets = useMemo(
    () => gadgets.map((ids) => ids.filter((id) => heldOptionIds.has(id))),
    [gadgets, heldOptionIds],
  );
  // Same reasoning as `liveGadgets`: an activation naming an Option the
  // robot has since lost is rejected by the engine ("no option X to
  // activate") and cannot be switched off from the UI, because the toggle
  // is only drawn for Options the robot holds.
  const liveActivated = useMemo(
    () => activated.filter((id) => heldOptionIds.has(id)),
    [activated, heldOptionIds],
  );
  // And the same for an aim: the engine rejects "no option X to aim", and
  // the four buttons that would clear it are only drawn for Options the
  // robot still holds.
  const liveAimed = useMemo(
    () => aimed.filter((a) => heldOptionIds.has(a.optionId)),
    [aimed, heldOptionIds],
  );

  const phase = view?.phase;

  // Follow the server's register as it advances, so playback keeps up.
  //
  // Every phase that belongs to a register counts, not just the two that
  // run one: the three mid-register prompts each carry theirs, and a page
  // loaded while one was open used to leave `shown` at 0 with nothing
  // marked as playing. A phase with no register clears it, so register 5
  // does not sit highlighted through the deal and the next programming --
  // but not while playback is still running, which is exactly when the
  // server has moved on and the player has not.
  useEffect(() => {
    if (phase && 'register' in phase) setShown(phase.register);
    else if (!playing) setShown(0);
  }, [phase, playing]);

  // Park the program as it is arranged, so an expired timer keeps the
  // cards already placed instead of filling all five registers at random.
  //
  // The engine fills only the registers a submission left EMPTY, so what
  // decides whether a player's work survives their time running out is
  // whether the server has it -- and until the player pressed Submit, the
  // server had nothing at all.
  //
  // An announced power down rides along for the same reason. It is set on
  // this screen and was sent only with a finished program, so a player who
  // asked to power down and then ran out of time was not powered down.
  //
  // Only while the player still has something to send: once submitted,
  // that is their final answer and the server refuses a draft over it.
  // Failures are swallowed; a draft is a safety net, and a player being
  // told their autosave failed while they are trying to program is worse
  // than the net not being there.
  //
  // `submitted` is this tab's memory and starts false on a reload or in a
  // second tab, so the server's `waitingFor` is asked as well: a robot
  // missing from it has a program in, and was being counted down at and
  // having drafts sent for regardless.
  const draftNeeded = phase?.kind === 'awaitingProgram'
    && !!me && phase.robotIds.includes(me.id) && !submitted
    && (phase.waitingFor === undefined || phase.waitingFor.includes(me.id));

  useEffect(() => {
    if (!draftNeeded) return undefined;
    // Debounced: a card dragged across five registers is one save, not
    // five, and the only deadline it has to beat is the phase's own.
    const timer = setTimeout(() => {
      void api.programDraft(id, submittedDraft, powerDown, {
        setAside,
        gadgets: liveGadgets,
        activated: liveActivated,
        aimed: liveAimed,
        // The turn a player is offered one, the facing is part of the
        // work the draft exists to keep. Without it a player who turned
        // their robot and then ran out of time -- or whose host started
        // the turn without them -- began pointing north.
        ...(picksStartFacing ? { facing: startFacing } : {}),
      }).catch(() => {});
    }, 1000);
    return () => clearTimeout(timer);
  }, [
    draftNeeded, id, submittedDraft, powerDown, setAside,
    liveGadgets, liveActivated, liveAimed, picksStartFacing, startFacing,
  ]);

  // A program the server holds but this tab did not send -- after a
  // reload, or in a second tab -- is fetched back and shown as sent, with
  // its cards. Without it the tableau opened blank with Submit live, and a
  // second submit replaced the first. Once per turn: taking a program back
  // puts the robot on `waitingFor` again, so this does not refire on it.
  const sentElsewhere = phase?.kind === 'awaitingProgram'
    && !!me && phase.robotIds.includes(me.id) && !submitted
    && phase.waitingFor !== undefined && !phase.waitingFor.includes(me.id);
  const restoredTurn = useRef<number | null>(null);
  useEffect(() => {
    if (!sentElsewhere || turn === undefined || restoredTurn.current === turn) return;
    restoredTurn.current = turn;
    void api.myProgram(id).then(({ program }) => {
      if (!program) return;
      setDraft(program.registers);
      setPowerDown(program.powerDown);
      if (program.facing) setStartFacing(program.facing);
      setSetAside(program.setAside ?? []);
      setGadgets(program.gadgets ?? [[], [], [], [], []]);
      setActivated(program.activated ?? []);
      setAimed(program.aimed ?? []);
      setSubmitted(true);
    }).catch(() => {});
  }, [sentElsewhere, turn, id]);

  const submitProgram = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.program(
        id, submittedDraft, powerDown,
        picksStartFacing ? startFacing : undefined,
        {
          setAside, gadgets: liveGadgets, activated: liveActivated,
          aimed: liveAimed,
        },
      );

      // A rejected submission comes back in the BODY with a 200, not as an
      // error. Treating the call succeeding as the program being accepted
      // left the player locked out with no way to resubmit.
      const problems = result.rejected?.flatMap((r) => r.problems) ?? [];
      if (problems.length > 0) {
        setError(`That program was not accepted: ${problems.join('; ')}`);
      } else {
        setSubmitted(true);
      }
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send your program.');
    } finally {
      setBusy(false);
    }
  };

  /** Puts the tableau back in the player's hands with the program intact.
   * The registers are local state and are left alone, so the cards do not
   * move; only whether they have been sent changes. */
  const unsubmitProgram = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.unsubmitProgram(id);
      setSubmitted(false);
      await load();
    } catch (err) {
      // A 409 is the turn having started while the button was on screen.
      // Nothing the player did wrong and nothing they can act on, so the
      // fresh state is the answer -- as with `sendAction` below.
      if (err instanceof ApiError && err.status === 409) {
        await load();
      } else {
        setError(err instanceof ApiError ? err.message : 'Could not take that back.');
      }
    } finally {
      setBusy(false);
    }
  };

  /** Returns whether the action was accepted, which the Recompile needs:
   * the draft is only worth clearing if the hand actually changed. */
  const sendAction = async (kind: string, choices?: unknown): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      await api.action(id, kind, choices, view?.version);
      await load();
      return true;
    } catch (err) {
      // A 409 means the game advanced since this screen last read it —
      // another player got there first, or an earlier click of ours did.
      // That is not something the player did wrong and not something they
      // can act on, so re-read rather than showing an error.
      if (err instanceof ApiError && err.status === 409) {
        await load();
      } else {
        setError(err instanceof ApiError ? err.message : 'Could not send that.');
      }
      return false;
    } finally {
      setBusy(false);
    }
  };

  /**
   * Recompile: a new hand for a point of damage, once per turn, before
   * the program goes in.
   *
   * The draft goes with the old hand -- every card in it has just been
   * discarded -- and so does anything set aside, which comes off the same
   * hand. `submitted` is cleared for the same reason: the button is not
   * offered after a submission, but a program parked before one is void
   * either way and the server drops it, so the screen must not go on
   * showing a program that is no longer this player's.
   */
  const recompile = async () => {
    if (!(await sendAction('recompile'))) return;
    setDraft([null, null, null, null, null]);
    setSetAside([]);
    setSubmitted(false);
  };

  // Nothing on the board, nothing to watch. Once every robot still in the
  // game is wrecked, the rest of the turn's registers move no one and the
  // "Continue to register N" button is pure ceremony: the player whose
  // robot was destroyed was being asked to click through three more
  // registers of an empty board before the turn would end. Advance for
  // them.
  //
  // Guarded on the version so the effect fires once per state rather than
  // once per render, and it deliberately waits for `playing` to finish so
  // the register that DID something is still played out in full.
  const autoAdvanced = useRef<number>(-1);
  const everyoneWrecked = Boolean(
    view && view.robots.length > 0 && view.robots.every((r) => r.destroyed),
  );
  useEffect(() => {
    if (!view || !isHost || busy || playing) return;
    if (view.phase.kind !== 'awaitingRegisterChoices') return;
    if (!everyoneWrecked) return;
    // A choice still to make outranks this: never skip past a prompt,
    // anyone's. Every robot being wrecked does not empty the chop shop
    // list -- a robot destroyed after it reached the shop is still owed
    // its answer.
    if ((view.phase.waitingFor ?? []).length > 0) return;
    if (me && view.phase.chopShop.includes(me.id)) return;
    if (autoAdvanced.current === view.version) return;
    autoAdvanced.current = view.version;
    void sendAction('registerChoices');
  }, [view, isHost, busy, playing, everyoneWrecked, me]);

  // A prompt that arrives MID-TURN, which is the kind a player misses:
  // the others come while they are already looking at the screen. Kept
  // apart from `waitingOnMe` below, which drives the banner's wording.
  //
  // Computed and hooked ABOVE the loading return: a hook below an early
  // return is not called on every render, which is the rules-of-hooks
  // violation React refuses to run.
  const promptedMidTurn = Boolean(view && me && view.phase && (
    (view.phase.kind === 'awaitingOptionUse' && view.phase.robotIds.includes(me.id))
    || (view.phase.kind === 'awaitingWeaponChoice' && view.phase.robotIds.includes(me.id))
    || (view.phase.kind === 'awaitingAbortChoice' && view.phase.robotIds.includes(me.id))
    || (view.phase.kind === 'awaitingRegisterChoices' && view.phase.chopShop.includes(me.id))
    || (view.phase.kind === 'awaitingEndOfTurn' && [
      ...view.phase.facing, ...view.phase.repair,
      ...view.phase.continuePowerDown, ...view.phase.returnPowerDown,
      ...(view.phase.discardOption ?? []),
    ].includes(me.id))
  ));
  // Waiting on the host to start the turn: programming has closed and
  // nobody can do anything until they click. The later registers get no
  // sound -- the host is already watching the playback they follow.
  const hostToStart = Boolean(
    view && isHost
      && view.phase.kind === 'runningRegister' && view.phase.register === 1,
  );
  // The programming clock, counted out loud over its last seconds -- and
  // only while this player still has a program to send. Once they have
  // submitted, the deadline is no longer theirs to beat.
  // And the turn whose programming this player owes, for the three beeps
  // that say it has opened. Not gated on `submitted`: a program taken back
  // is the same turn and should not sound again.
  const programmingTurn = phase?.kind === 'awaitingProgram'
    && !!me && phase.robotIds.includes(me.id) ? view?.turnNumber ?? null : null;
  const beep = useTurnBeep(
    promptedMidTurn, hostToStart,
    draftNeeded ? (view?.deadline ?? null) : null,
    programmingTurn,
  );

  if (!view) {
    return (
      <main className="content">
        <p className="eyebrow">Loading</p>
        {error && <p className="notice">{error}</p>}
      </main>
    );
  }

  /** Runs a host override. Shares `busy` and `error` with `sendAction` so
   * two forcing clicks cannot overlap, and so a refusal from the server --
   * not the host, or nothing waiting -- is shown rather than swallowed. */
  const runForce = async (call: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await call();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'could not force that');
    } finally {
      setBusy(false);
    }
  };

  const waitingOnMe =
    (phase?.kind === 'awaitingProgram' && me && phase.robotIds.includes(me.id) && !submitted) ||
    (phase?.kind === 'awaitingPowerDown' && me && phase.robotIds.includes(me.id));


  return (
    <main className="game">
      {/* The board and its key in one stack, so the side panel's height
          cannot come between them -- see `.game-main`. */}
      <div className="game-main">
        <div className="game-board-pane">
          {layout ? (
            <Board
              layout={layout}
              robots={drawnRobots}
              colours={colours}
              youAre={view.youAre}
              epoch={`${view.turnNumber}:${
                'register' in view.phase ? view.phase.register : view.phase.kind
              }`}
              nameOf={nameOf}
              tokens={shownTokens}
              shot={shownShot}
              flags={shownFlags}
              wrecked={shownWrecks}
              // Entering a replay and leaving it are cuts: the board jumps
              // to another turn and back, and a robot must not be seen
              // driving the length of the course to get there.
              cut={replay.entry ? `replay:${replay.entry.seq}` : 'live'}
              cellPx={cellPx}
            />
          ) : (
            <p className="eyebrow">Loading the course</p>
          )}
        </div>

        {/* The key to what is drawn on the board, under it and OUTSIDE the
            board's pane. Inside it, the two shared one scroll: reading the
            key on a tall course meant scrolling the board out of view,
            which is the one thing the key is looked at alongside. */}
        {layout && (
          <BoardKey
            elements={layout.elements ?? []}
            tokens={shownTokens}
            debris={view.debris}
          />
        )}
        {settings && <GameSettings settings={settings} />}
      </div>

      <aside className="game-side">
        {/* The heading and the phase line together, so the grid gap
            falls outside the pair rather than between them. The line is
            a caption for the turn -- who the table is waiting on -- and
            belongs against the heading, above the hazard bar, rather
            than adrift below the register strip. */}
        <div className="game-heading">
          <div className="spread">
            <div>
              {/* Who you are, beside the turn. With several browsers open
                  on one machine to test a multiplayer game, the screens are
                  otherwise indistinguishable. */}
              <p className="eyebrow">
                Turn {view.turnNumber}
                {view.youAre && (
                  <>
                    {' / '}
                    <span style={{ color: colours.get(view.youAre) }}>
                      {nameOf(view.youAre)}
                    </span>
                  </>
                )}
              </p>
              <h3>{layout?.name ?? `Game ${id}`}</h3>
            </div>
            {/* Neither of these is part of playing, so they are set
                smaller than a button that does something to the game. At
                full size the pair took as much of the heading as the
                course name. */}
            <div className="row game-tools" style={{ gap: '0.4rem' }}>
              {/* A sound with no way to switch it off is worse than no
                  sound. The preference is per browser and survives a
                  reload.

                  The colour is the state and the label stays put: a
                  button that reads "Beep off" is ambiguous about whether
                  that is what it IS or what it DOES. */}
              <button
                className={beep.muted ? 'state-off' : 'state-on'}
                onClick={() => beep.setMuted(!beep.muted)}
                title={beep.muted
                  ? (isHost
                    ? 'Beep is off. Switch it on to hear when it is your turn to decide, lower when register 1 is yours to start, and the last ten seconds of the programming clock'
                    : 'Beep is off. Switch it on to hear when it is your turn to decide, and the last ten seconds of the programming clock')
                  : 'Beep is on. Silence it'}
                aria-pressed={!beep.muted}
              >
                Beep
              </button>
              <button className="secondary" onClick={() => navigate('/')}>Leave</button>
            </div>
          </div>

          <PhaseBanner
            phase={phase}
            waitingOnMe={Boolean(waitingOnMe)}
            shown={shown}
            view={view}
            nameOf={nameOf}
          />
        </div>

        <div className="hazard hazard-thin" role="presentation" />

        {error && <p className="notice">{error}</p>}

        {/* Your own program, visible as it executes. Without this the
            playback shows robots moving with no way to see which card is
            responsible until after the fact. */}
        {/* NOT the three mid-register prompts. A SECOND strip below covers
            those, highlighting the register the decision is about rather
            than the lagging `shown`. Adding them here on 2026-08-19 --
            on a wrong reading that the strip vanished during a prompt --
            drew both, and the player saw two identical rows of cards. */}
        {me && (phase?.kind === 'runningRegister'
          || phase?.kind === 'awaitingRegisterChoices') && (
          <RegisterStrip
            registers={me.registers}
            current={shown}
            options={me.options ?? []}
            locked={me.lockedRegisters}
          />
        )}

        {replay.replaying && (
          <p className="notice ok">
            Replaying turn {replay.entry?.turn}, register {replay.entry?.register}.
            {' '}
            <button className="secondary log-replay" onClick={replay.stop}>stop</button>
          </p>
        )}

        {phase?.kind === 'awaitingProgram' && (
          <p className="spread">
            <Countdown deadline={view.deadline} paused={view.timerPaused} />
            {/* The host's, and only while there is a clock to stop: turn
                1 is never timed and a game may have no timer at all, and
                a button that stops nothing is worse than no button. */}
            {isHost && (view.deadline !== null || view.timerPaused) && (
              <button
                className="secondary"
                disabled={busy}
                onClick={() => void runForce(
                  () => api.setTimerPaused(id, !view.timerPaused),
                )}
              >
                {view.timerPaused ? 'Start the clock' : 'Stop the clock'}
              </button>
            )}
          </p>
        )}

        {/* Shown to whoever the phase is WAITING ON, not to whoever holds
            cards. A robot on 9 damage is dealt none and has all five
            registers locked: it has a full program to look at and a power
            down to decide, and it still owes the table a submission. The
            old test was `hand.length > 0`, so that player got the "nothing
            to program" line instead -- no registers, no submit button --
            and the turn could not be finished by anyone. Being waited on
            is the real question, and `robotIds` already answers it: it
            leaves out the destroyed, the eliminated and the powered down,
            who are exactly the ones with nothing to do here. */}
        {phase?.kind === 'awaitingProgram' && me && (
          phase.robotIds.includes(me.id) ? (
            <Programmer
              hand={me.hand ?? []}
              locked={locked}
              options={me?.options ?? []}
              setAside={setAside}
              onSetAsideChange={setSetAside}
              gadgets={liveGadgets}
              onGadgetsChange={setGadgets}
              activated={liveActivated}
              onActivatedChange={setActivated}
              aimed={liveAimed}
              onAimedChange={setAimed}
              virtual={me.virtual ?? false}
              registers={workingDraft}
              onChange={setDraft}
              onSubmit={() => void submitProgram()}
              submitting={busy}
              submitted={submitted}
              onUnsubmit={() => void unsubmitProgram()}
              gained={gainedOptions}
              powerDown={powerDown}
              onPowerDownChange={setPowerDown}
              needsFacing={picksStartFacing}
              facing={startFacing}
              onFacingChange={setStartFacing}
              recompile={
                me.recompiledThisTurn
                  ? undefined
                  : (me.options ?? []).find((o) => o.effect === 'recompile')
              }
              onRecompile={() => void recompile()}
            />
          ) : (
            <p className="muted">
              {/* Reached only by a robot the phase is not waiting on: it is
                  powered down, destroyed or out. The powered-down case says
                  why; the rest keep the bare wording rather than being told
                  something untrue about themselves. */}
              {me.poweredDown
                ? 'Your robot is powered down.'
                : 'You have no cards this turn. Nothing to program.'}
            </p>
          )
        )}

        {/* Kept for a game that somehow reaches this phase without a
            parked answer -- normally the server applies the choice made on
            the programming screen and this never appears. A program forced
            through is how it happens: nobody filled that screen in, so
            there was nothing to park.

            The SAME control as the programming screen rather than a pair
            of buttons of its own. It is one question asked in two places
            and should not read as two different decisions. */}
        {phase?.kind === 'awaitingPowerDown' && me && phase.robotIds.includes(me.id) && (
          <div className="stack">
            <PowerDownChoice
              value={powerDown}
              onChange={setPowerDown}
              disabled={busy}
            />
            <button
              disabled={busy}
              onClick={() => void sendAction('powerDown', { powerDown })}
            >
              Submit
            </button>
          </div>
        )}

        {/* The programmed cards, during any choice made mid-register.
            A player choosing Brakes needs to see what is coming next.

            Highlighting the register the DECISION is about, which is the
            phase's own — `shown` deliberately lags the server so playback
            can animate, so during a choice about register 4 it is still
            showing 3. */}
        {me && (phase?.kind === 'awaitingOptionUse'
          || phase?.kind === 'awaitingAbortChoice'
          || phase?.kind === 'awaitingWeaponChoice') && (
          <RegisterStrip
            registers={me.registers}
            current={phase.register}
            options={me.options ?? []}
            locked={me.lockedRegisters}
          />
        )}

        {/* A run-time Option, asked as the register is about to run and in
            priority order, so a player decides in the order their card
            will execute. */}
        {phase?.kind === 'awaitingOptionUse' && me && (
          phase.robotIds[0] === me.id ? (
            <RunTimeOption
              me={me}
              register={phase.register}
              busy={busy}
              onDecide={(use) => void sendAction('optionUse', { use })}
            />
          ) : (
            // Named, not "another player". The queue is in PRIORITY
            // order and `robotIds[0]` is the one actually being asked, so
            // there is a specific answer and it was being withheld. That
            // you are behind them is worth keeping: it says your own turn
            // to decide is coming.
            <p className="muted">
              Waiting for {nameOf(phase.robotIds[0])} to use an option
              {phase.robotIds.includes(me.id) ? ', ahead of you.' : '.'}
            </p>
          )
        )}

        {/* The Abort Switch, asked at reveal — before anyone can see what
            anybody else played, and before the turn order exists, since
            the card drawn brings its own priority. */}
        {phase?.kind === 'awaitingAbortChoice' && me && (
          phase.robotIds.includes(me.id) ? (
            <div className="stack" style={{ gap: '0.4rem' }}>
              <p className="notice">
                Decide before your card for register {phase.register} is
                revealed.
              </p>
              <div className="row">
                {/* Doing nothing comes FIRST and is worded the same here
                    as in every other per-register prompt. It is the
                    default action and the one a player picks most, so it
                    should not have to be hunted for at the end of a row
                    whose length depends on which Options they hold. */}
                <button
                  onClick={() => void sendAction('abortChoice', { abort: false })}
                  disabled={busy}
                >
                  Play card in register
                </button>
                {/* Holding the switch is not enough: once thrown it is
                    thrown for the turn and the choice cannot be changed,
                    so offering the button again offers something that
                    cannot happen. The robot is still asked at later
                    registers, but only about its Conditional. */}
                {(me.options ?? []).some((o) => o.effect === 'abortSwitch')
                  && !me.abortSwitchActive && (
                  <button
                    className="secondary"
                    onClick={() => void sendAction('abortChoice', { abort: true })}
                    disabled={busy}
                  >
                    Throw the Abort Switch
                  </button>
                )}
                {(() => {
                  // Name the card. The player set it aside turns ago and
                  // is otherwise deciding blind about their own option.
                  const heldCard = (me.options ?? [])
                    .find((o) => o.effect === 'conditional' && o.heldCard)?.heldCard;
                  if (!heldCard) return null;
                  return (
                    <button
                      className="secondary"
                      onClick={() => void sendAction('abortChoice', {
                        abort: false, substitute: true,
                      })}
                      disabled={busy}
                    >
                      Play my Conditional: {CARD_LABEL[heldCard.type] ?? heldCard.type}
                      {' '}{heldCard.priority}
                    </button>
                  );
                })()}
              </div>
            </div>
          ) : (
            // Named, as the weapon wait is: `robotIds` drops each robot as
            // its answer arrives.
            <p className="muted">
              Waiting for {namesOf(phase.robotIds)} to decide before the reveal.
            </p>
          )
        )}

        {/* Which weapon to fire, asked before laser fire. Everyone is
            asked at once: weapons fire simultaneously, so there is
            nothing to learn by answering after someone else. */}
        {phase?.kind === 'awaitingWeaponChoice' && me && (
          phase.robotIds.includes(me.id) ? (
            <WeaponChoice
              me={me}
              busy={busy}
              onChoose={(weapon, fireControl) => void sendAction(
                'weaponChoice', { weapon, fireControl },
              )}
              usable={phase.usable?.[me.id] ?? []}
              aimable={phase.fireControl?.[me.id] ?? []}
              robots={view.robots}
              nameOf={nameOf}
            />
          ) : (
            // Named: `robotIds` is rebuilt without each answer as it
            // arrives, so it is exactly who is still choosing. A line of
            // text rather than a button, so every name fits.
            <p className="muted">
              Waiting for {namesOf(phase.robotIds)} to choose a weapon.
            </p>
          )
        )}

        {/* The turn's first register waits for a click, exactly as every
            later one does. Before this it ran the moment programming
            closed, which made register 1 the odd one out. */}
        {phase?.kind === 'runningRegister' && phase.register === 1 && (
          <div className="stack">
            {isHost ? (
              <button
                onClick={() => void sendAction('startRegisters')}
                disabled={busy}
              >
                Start register 1
              </button>
            ) : (
              <p className="muted">Waiting for the host to start register 1.</p>
            )}
          </div>
        )}

        {phase?.kind === 'awaitingRegisterChoices' && (() => {
          // Robots at a chop shop that have not answered yet. The host's
          // own pick counts as answered the moment it is made, since it
          // travels with the advance rather than being parked.
          //
          // Gated, like the end of turn. The host advancing over a
          // silent player chose FOR them -- nothing on registers 1 to 4,
          // and on register 5 an option drawn without them seeing what it
          // was -- and doing it took one click, before anyone had time to
          // read the panel. Declining is now a button, so a player who
          // wants none of it can say so and the table moves on.
          const outstanding = phase.waitingFor ?? [];
          const stillDeciding = outstanding.filter(
            (id) => !(isHost && me && id === me.id && chopChoice),
          );
          // The host's own outstanding pick is separated from everyone
          // else's: a button reading "Waiting for Rick" put to Rick is a
          // riddle. It is the same gate either way, said the way round
          // that tells the reader what to do about it.
          const waitingOnMine = Boolean(me && stillDeciding.includes(me.id));
          const others = stillDeciding.filter((id) => id !== me?.id);
          // WHO, not how many, exactly as the end-of-turn button says it.
          const waitingOn = others.length <= 2
            ? others.map(nameOf).join(' and ')
            : `${others.length} players`;

          // The server's word that this player's pick is parked. Never
          // the host's: theirs travels with their own advance below, so
          // they stay on the list until the register moves.
          const chopSent = Boolean(!isHost && me && chopChoice
            && !outstanding.includes(me.id));

          // What the PREVIOUS register's checkpoints did for this robot.
          // The result of a register belongs to that register. It is
          // resolved by the advance that leaves that register's pause, so
          // this pause -- the next one -- is the first moment it can be
          // shown, and showing it here is what stops it queueing up to
          // arrive with everything else on the programming screen.
          const lastRegister = awardsFrom(view.turnNumber, [phase.register - 1]);

          return (
          <div className="stack">
            {lastRegister.length > 0 && (
              <p className="notice ok upgrade">
                {lastRegister.map((a, i) => (
                  <span key={`${a.kind}-${a.name}`}>
                    {i > 0 && <br />}
                    {`Register ${phase.register - 1}: `}
                    {a.kind === 'swapped'
                      ? (a.gaveUp
                        ? <>you gave up {a.gaveUp} for <strong>{a.name}</strong></>
                        : <>you swapped to <strong>{a.name}</strong></>)
                      : a.kind === 'reloaded'
                        ? <>you reloaded <strong>{a.name}</strong></>
                        : <>you took <strong>{a.name}</strong></>}
                  </span>
                ))}
              </p>
            )}
            {/* A chop shop does ONE of three things, and the player
                picks which. The stale message here said Options were not
                in play, which stopped being true some time ago. */}
            {me && phase.chopShop.includes(me.id) && (
              <ChopShop
                me={me}
                busy={busy}
                sent={chopSent}
                chosen={chopChoice}
                // A pick here IS the choice -- there is no further step
                // that could still change it, unlike the program tray
                // where a card can be dragged elsewhere before submit.
                // The host's pick still only travels on their own
                // advance below, since sending it here would advance the
                // register out from under whoever else is still deciding.
                // A non-host has no such button to press, so their pick
                // is sent the moment it is made; the server only parks
                // it (see http.ts, registerChoices), so re-picking before
                // the host advances just overwrites the parked row.
                onChoose={(next) => {
                  setChopChoice(next);
                  if (!isHost) void sendAction('registerChoices', next ? { chopShop: next } : {});
                }}
                register={phase.register}
                optionsAvailable={view.optionsAvailable}
              />
            )}

            {isHost ? (
              <button
                onClick={() => void sendAction('registerChoices', {
                  ...(chopChoice ? { chopShop: chopChoice } : {}),
                })}
                // A player still deciding holds the register. Nobody who
                // cannot answer is on the list: the phase leaves out
                // robots the shop can do nothing for and robots that are
                // powered down, and the view drops anyone the host has
                // removed from the game.
                disabled={busy || playing || stillDeciding.length > 0}
                title={
                  stillDeciding.length > 0
                    ? `Still to answer: ${stillDeciding.map(nameOf).join(', ')}`
                    : undefined
                }
              >
                {/* "Finish the turn" was a lie: this button does not
                    finish anything, it advances INTO the end-of-turn
                    phase, whose own button then read "End the turn". Two
                    synonyms for two different steps. Both now say where
                    they go, and this one parallels its own siblings. */}
                {waitingOnMine
                  ? 'Answer the chop shop first'
                  : others.length > 0
                    ? `Waiting for ${waitingOn}`
                    : phase.register < 5
                      ? `Continue to register ${phase.register + 1}`
                      : 'Continue to end of turn'}
              </button>
            ) : (
              // Only the host advances, so one player cannot pull the
              // table forward and cut short everyone else's playback.
              <p className="muted">
                {playing
                  ? 'Playing back the register.'
                  : me?.poweredDown
                    // A powered-down robot is doing nothing for the whole
                    // turn, so say so rather than leaving the player
                    // wondering what they are waiting to do.
                    ? 'Powered down. Waiting for the host to continue. Grab a snack!'
                    : 'Waiting for the host to continue.'}
              </p>
            )}

            {/* Named, because the host cannot go on without them and the
                only way out is to ask. This used to say what continuing
                would take for them, back when continuing was allowed. */}
            {isHost && others.length > 0 && (
              <p className="mono muted" style={{ fontSize: '0.75rem' }}>
                The register waits on {others.map(nameOf).join(', ')}.
              </p>
            )}
          </div>
          );
        })()}

        {phase?.kind === 'awaitingEndOfTurn' && (
          <EndOfTurn
            phase={phase}
            me={me}
            busy={busy}
            isHost={isHost}
            optionsAvailable={view.optionsAvailable}
            nameOf={nameOf}
            onSend={(choices) => void sendAction('endOfTurn', choices)}
          />
        )}

        {phase?.kind === 'gameOver' && (
          <div className="stack">
            <h2>
              {phase.winnerId === null
                ? 'Everyone is out'
                : phase.winnerId === view.youAre
                  ? 'You win'
                  : `${nameOf(phase.winnerId)} wins`}
            </h2>
            {phase.reason === 'allEliminated' && (
              <p className="muted">
                Every robot ran out of life tokens. Nobody reached the last flag.
              </p>
            )}
            <button onClick={() => navigate('/')}>Back to the lobby</button>
          </div>
        )}

        <Standings
          view={view}
          colours={colours}
          nameOf={nameOf}
          shown={shown}
          isHost={isHost}
          busy={busy}
          onRemove={(robotId) => {
            // `players` already maps robot to user, so no extra fetch.
            const target = players.find((p) => p.robot_id === robotId);
            if (!target) return;
            void runForce(async () => {
              await api.removePlayer(id, target.user_id);
              setPlayers(await api.players(id).then((r) => r.players));
            });
          }}
        />

        {/* Host overrides, for a player who has dropped. Kept together
            immediately above the log rather than beside the prompt each
            one answers: they are host business, they are the last resort,
            and scattering them meant the host had to hunt for a different
            place on the page depending on which phase had stalled.

            Deliberately plain and unhighlighted: they spend other
            people's cards and answer other people's prompts, so they
            should look like the last resort they are rather than an
            ordinary button. Each still appears only in the phase it can
            act on, so at most one is ever on screen, and only when there
            is somebody other than the host still being waited on -- see
            `waitingOnSomeoneElse`. */}
        {isHost && phase?.kind === 'awaitingProgram'
          && waitingOnSomeoneElse(phase.robotIds) && (
          <button
            className="secondary"
            disabled={busy}
            title={'Fills any register a player left empty at random from '
              + "that player's own hand"}
            onClick={() => void runForce(() => api.forceProgram(id))}
          >
            Start the turn without everyone
          </button>
        )}

        {isHost && phase?.kind === 'awaitingPowerDown'
          && waitingOnSomeoneElse(phase.robotIds) && (
          <button
            className="secondary"
            disabled={busy}
            title={'Anyone who has not answered is recorded as NOT powering down'}
            onClick={() => void runForce(() => api.forcePowerDown(id))}
          >
            Skip the power-down step
          </button>
        )}

        {isHost && (phase?.kind === 'awaitingOptionUse'
          || phase?.kind === 'awaitingWeaponChoice'
          || phase?.kind === 'awaitingAbortChoice')
          && waitingOnSomeoneElse(phase.robotIds) && (
          <button
            className="secondary"
            disabled={busy}
            title="Declines on behalf of anyone who has not answered"
            onClick={() => void runForce(() => api.forceRegister(id))}
          >
            Answer for the missing players
          </button>
        )}

        {/* The chop-shop pause holds the register the same way, and the
            same button clears it. It advances the register as well, since
            answering the shop is the only thing left to do at that
            pause. */}
        {isHost && phase?.kind === 'awaitingRegisterChoices'
          && waitingOnSomeoneElse(phase.waitingFor ?? []) && (
          <button
            className="secondary"
            disabled={busy || playing}
            title={'Anyone who has not answered takes the default the shop '
              + 'offers: an option at register 5, nothing before it'}
            onClick={() => void runForce(() => api.forceRegister(id))}
          >
            Answer the chop shop for the missing players
          </button>
        )}

        {isHost && phase?.kind === 'awaitingEndOfTurn'
          && waitingOnSomeoneElse(phase.waitingFor ?? []) && (
          <button
            className="secondary"
            disabled={busy}
            title={'Anyone who has not answered keeps their facing, heals '
              + 'rather than taking an option, and does not power down'}
            onClick={() => void runForce(() => api.forceEndOfTurn(id))}
          >
            End the turn without everyone
          </button>
        )}

        {/* Nobody to talk to at a table of one. */}
        {players.length > 1 && (
          <ChatPanel
            messages={chat}
            robotIds={players.map((p) => p.robot_id)}
            youAre={view.youAre}
            nameOf={nameOf}
            onSend={async (to, text) => addChat((await api.sendChat(id, to, text)).message)}
          />
        )}

        <LogPanel
          entries={log.entries}
          robotIds={view.robots.map((r) => r.id)}
          youAre={view.youAre}
          nameOf={nameOf}
          onReplay={replay.start}
          replayingSeq={replay.entry?.seq ?? null}
          onReplayTurn={(turn) => replay.startTurn(log.entries.filter((e) => e.turn === turn))}
          replayingTurn={replay.turn}
        />
      </aside>
    </main>
  );
}

/**
 * What to do at a chop shop: reload one launcher, exchange one Option for
 * a fresh one, or take a new one.
 *
 * One of the three, not all of them — it used to reload every launcher
 * the robot held, unconditionally, which is both too generous and no
 * choice at all. One of the three PER REGISTER: the choice is made again,
 * from scratch, at every register the robot ends on the shop.
 */
function ChopShop({
  me, busy, sent, chosen, onChoose, register, optionsAvailable,
}: {
  me: RobotView;
  busy: boolean;
  /** Whether this pick is already parked on the server. A non-host sends
   * the moment they click, and nothing said so: the panel sat there
   * exactly as it had before, which on register 5 with nothing held --
   * one button, already showing as picked -- left no way at all to tell
   * the send had worked. */
  sent: boolean;
  chosen: { action: 'reload' | 'exchange' | 'draw' | 'nothing'; optionId?: string } | null;
  onChoose: (next: { action: 'reload' | 'exchange' | 'draw' | 'nothing'; optionId?: string } | null) => void;
  /** Which register just resolved. Taking a NEW option is register 5 only. */
  register: number;
  /** Option cards a draw could produce: the deck plus the discard pile,
   * which reshuffles in when the deck runs dry. Zero with Options off. */
  optionsAvailable: number;
}) {
  const held = me.options ?? [];
  // Only a launcher that has actually spent something is worth reloading.
  const reloadable = held.filter(
    (o) => o.remaining !== undefined && o.remaining < (o.payload ?? 0),
  );

  // What this robot could actually do here, right now.
  const canDraw = register === 5 && optionsAvailable > 0;
  const canExchange = held.length > 0 && optionsAvailable > 0;
  const canReload = reloadable.length > 0;

  // Nothing starts selected. Register 5 used to open with "take a new
  // option" already lit, because an unanswered prompt took one anyway --
  // but the host now waits for an answer, and a button that looks
  // answered when nothing has been sent is the one way to leave a table
  // waiting on a player who believes they have already replied.
  const shown = chosen;

  // Nothing on offer: show nothing at all. The engine now leaves such a
  // robot out of the phase's `chopShop` list, so this should be
  // unreachable -- it stays as a guard rather than as a panel, because a
  // prompt whose only content is that there is no prompt is worse than
  // no prompt. The visit is reported to the log instead.
  if (!canDraw && !canExchange && !canReload) return null;

  return (
    <div className="stack" style={{ gap: '0.4rem' }}>
      <p className="eyebrow">Chop shop</p>

      {/* The only thing on the screen that says the pick went anywhere.
          The buttons grey out at the same moment. What is being waited
          for is said once, below the panel. */}
      {sent && <p className="notice ok">Choice sent.</p>}

      {/* Taking a new option, giving up nothing, is register 5 only.
          Reload and exchange are available on any register, so the panel
          is still worth showing without that button at all.

          THE TWO ARE EXCLUSIVE. Declining is a real answer while the
          shop is only offering to reload or to swap -- the host waits, so
          without a button for "none of this" the only way to say so is
          silence, and silence is indistinguishable from a player who has
          walked away. There is nothing to decline once a free card is on
          the counter: the answers are reload, swap, or take it, and no
          answer takes it. */}
      <div className="row">
        {canDraw ? (
          <button
            className={shown?.action === 'draw' ? '' : 'secondary'}
            onClick={() => onChoose({ action: 'draw' })}
            disabled={busy || sent}
          >
            Take a new option
          </button>
        ) : (
          <button
            className={shown?.action === 'nothing' ? '' : 'secondary'}
            onClick={() => onChoose({ action: 'nothing' })}
            disabled={busy || sent}
            title="Use the shop for nothing this register"
          >
            Do nothing
          </button>
        )}
      </div>

      {canReload && (
        <div className="stack" style={{ gap: '0.2rem' }}>
          <span className="mono muted" style={{ fontSize: '0.75rem' }}>Reload one</span>
          <div className="option-tray">
            {reloadable.map((option, i) => (
              <button
                key={`${option.id}-${i}`}
                className={
                  shown?.action === 'reload' && shown.optionId === option.id
                    ? 'option-chip chosen' : 'option-chip'
                }
                onClick={() => onChoose({ action: 'reload', optionId: option.id })}
                disabled={busy || sent}
                title={option.text}
              >
                {option.name}
                <span className="option-payload num"> {option.remaining}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {canExchange && (
        <div className="stack" style={{ gap: '0.2rem' }}>
          <span className="mono muted" style={{ fontSize: '0.75rem' }}>
            Select one option to scrap and draw a new one
          </span>
          <div className="option-tray">
            {held.map((option, i) => (
              <button
                key={`${option.id}-${i}`}
                className={
                  shown?.action === 'exchange' && shown.optionId === option.id
                    ? 'option-chip chosen' : 'option-chip'
                }
                onClick={() => onChoose({ action: 'exchange', optionId: option.id })}
                disabled={busy || sent}
                title={option.text}
              >
                {option.name}
                {/* Shots left, exactly as the reload tray shows them:
                    giving up a launcher is a different decision when it
                    is empty than when it is full. */}
                {option.remaining !== undefined && (
                  <span className="option-payload num"> {option.remaining}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Which weapon to fire this register.
 *
 * An optional weapon is fired INSTEAD of the main laser, so this is a
 * genuine either/or rather than an extra.
 */
function WeaponChoice({
  me, busy, onChoose, usable, aimable, robots, nameOf,
}: {
  me: RobotView;
  busy: boolean;
  onChoose: (
    weapon: string | null,
    fireControl?: { targetId: string; register?: number; optionId?: string },
  ) => void;
  /** Effects this robot can actually fire, from the engine. */
  usable: string[];
  /** Who the main laser would hit, in beam order. Empty unless this robot
   * holds Fire Control. */
  aimable: string[];
  robots: RobotView[];
  nameOf: (robotId: string) => string;
}) {
  // Only what could be fired. Holding one weapon that needs no target
  // used to open the whole menu, offering a Pressor Beam with nothing in
  // front of it to push.
  const weapons = (me.options ?? []).filter(
    (o) => o.timing === 'optionalWeapon' && usable.includes(o.effect),
  );

  // Fire Control aims at one robot in the beam. The first is the one the
  // beam reaches first, and with no High Power Laser it is the only one.
  const [aimId, setAimId] = useState(aimable[0] ?? '');
  const aimed = robots.find((r) => r.id === aimId);
  // Only the registers still open. Locking one that is already locked
  // does nothing at all -- the point lands as damage either way -- so
  // offering it is offering a wasted shot. A robot with all five locked
  // shows none, and the option half of the card is all that is left.
  const openRegisters = [1, 2, 3, 4, 5].filter(
    (register) => !(aimed?.lockedRegisters ?? [])[register - 1],
  );

  return (
    <div className="stack" style={{ gap: '0.4rem' }}>
      <p className="eyebrow">Fire which weapon?</p>

      {/* Fire Control is not a second decision. It says what ONE point of
          the MAIN LASER's damage is for, so it belongs under the laser as
          the way that shot is aimed. Asked side by side, picking the
          laser ruled Fire Control out. Firing the laser plain is the
          first entry in the same list, not a separate button above it. */}
      {aimed && aimable.length > 1 && (
        <select
          value={aimId}
          onChange={(e) => setAimId(e.target.value)}
          disabled={busy}
          title="Which robot in the beam the shot is aimed at"
        >
          {aimable.map((id) => (
            <option key={id} value={id}>{nameOf(id)}</option>
          ))}
        </select>
      )}

      <div className="row">
        <button
          onClick={() => onChoose(null)}
          disabled={busy}
          title={aimed
            ? 'Fire the main laser as usual. The damage lands where the '
              + 'damage table puts it.'
            : undefined}
        >
          {aimed ? 'Main laser, no control' : 'Main laser'}
        </button>

        {aimed && openRegisters.map((register) => (
          <button
            key={register}
            className="secondary"
            onClick={() => onChoose(null, { targetId: aimId, register })}
            disabled={busy}
            title={`The damage still lands. It locks register ${register} `
              + `on ${nameOf(aimId)} instead of the register the damage `
              + 'table would have locked.'}
          >
            Lock {register}
          </button>
        ))}

        {aimed && (aimed.options ?? []).map((option) => (
          <button
            key={option.id}
            className="secondary"
            onClick={() => onChoose(null, { targetId: aimId, optionId: option.id })}
            disabled={busy}
            title={`${option.text} Destroying it uses the point of damage `
              + 'up, and anything left on the card goes off in that square.'}
          >
            Destroy {option.name}
          </button>
        ))}
      </div>

      {/* The other weapons are a real alternative to the laser, so they
          keep a row of their own rather than sitting among the ways of
          aiming it. */}
      {weapons.length > 0 && (
        <div className="row">
          {weapons.map((weapon) => (
            <button
              key={weapon.id}
              className="secondary"
              onClick={() => onChoose(weapon.effect)}
              disabled={busy}
              title={weapon.text}
            >
              {weapon.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The one run-time decision a player has, at the moment it matters.
 *
 * Only ever shown to the player at the front of the queue: the phase asks
 * in priority order, one at a time, so nobody answers out of turn.
 */
function RunTimeOption({
  me, register, busy, onDecide,
}: {
  me: RobotView;
  register: number;
  busy: boolean;
  onDecide: (use: string[]) => void;
}) {
  const card = slotCard(me.registers[register - 1]);
  const usable = (me.options ?? []).filter((option) => (
    (option.effect === 'fourthGear' && card?.type === 'Move3')
    || (option.effect === 'brakes' && card?.type === 'Move1')
    || (option.effect === 'reverseGears' && card?.type === 'BackUp')

  ));

  if (usable.length === 0) {
    // Should not happen — the engine only asks robots that have one — but
    // a dead end here would stall the whole table.
    return (
      <button onClick={() => onDecide([])} disabled={busy}>Continue</button>
    );
  }

  return (
    <div className="stack" style={{ gap: '0.4rem' }}>
      <p className="notice ok">
        Register {register}. {usable[0].name}: {usable[0].text}
      </p>
      <div className="row">
        {/* Same wording and same position as every other per-register
            prompt: doing nothing is first. */}
        <button onClick={() => onDecide([])} disabled={busy}>
          Play card in register
        </button>
        <button
          className="secondary"
          onClick={() => onDecide([usable[0].effect])}
          disabled={busy}
        >
          Use {usable[0].name}
        </button>
      </div>
    </div>
  );
}

function RegisterStrip({
  registers, current, options = [], locked = [],
}: {
  registers: (RegisterSlot | null)[];
  current: number;
  /**
   * The Options held by the robot these registers belong to, so a gadget
   * can be named rather than shown as its id. Defaults to none, which
   * falls back to a readable form of the id.
   */
  options?: OptionCardView[];
  /**
   * Which registers are locked, marked exactly as the programming screen
   * marks them.
   *
   * The strip showed the same five registers and said nothing about this,
   * so a card the player never chose looked like one they did. It is not
   * idle information while the strip is up: nothing in the engine spares
   * a locked register, so an Abort Switch or a Conditional substitution
   * replaces its card like any other -- and the lock then holds the NEW
   * card at End of Turn. Public, like the damage that causes it.
   */
  locked?: boolean[];
}) {
  return (
    <div className="strip" role="list">
      {registers.map((slot, i) => {
        // The priority card is what the strip shows. A register holding
        // more than one card still runs at the first card's priority.
        const card = slotCard(slot);
        const gadgets = slot?.gadgets ?? [];
        return (
        <div
          key={i}
          role="listitem"
          className={`strip-slot${i + 1 === current ? ' now' : ''}`
            + `${i + 1 < current ? ' done' : ''}`}
        >
          <span className="strip-number num">{i + 1}</span>
          {locked[i] && <span className="slot-lock">Locked</span>}
          {card ? (
            <>
              {/* Priority above the effect, matching the cards in hand. */}
              <span className="strip-priority num">{card.priority}</span>
              <span className="strip-name">{CARD_LABEL[card.type] ?? card.type}</span>
            </>
          ) : (
            <span className="strip-name muted">-</span>
          )}

          {/* What was loaded into this register, named exactly as the
              programming screen names it. A gadget fires BEFORE the
              register's card, so it has to be readable while the register
              is being played and not only while it is being written.
              Independent of whether the CARD is showing: a register can
              be blank here (an Abort Switch hides what has not run yet)
              and still have a gadget the player put there themselves. */}
          {gadgets.length > 0 && (
            <span className="slot-gadgets">
              {gadgets.map((id) => (
                <span key={id} className="slot-gadget">
                  {options.find((o) => o.id === id)?.name ?? readableId(id)}
                </span>
              ))}
            </span>
          )}
        </div>
        );
      })}
    </div>
  );
}

/**
 * A camelCase Option id as something a player can read.
 *
 * Only reached when the Option itself is gone -- traded at a chop shop,
 * or given up on destruction -- while the register that used it is still
 * on screen. Better than printing `gooDropper` at somebody.
 */
function readableId(id: string): string {
  const spaced = id.replace(/([A-Z])/g, ' $1').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * One line under the turn heading saying what the game is waiting for.
 *
 * Wrapped rather than returned bare so the spacing has a single hook: a
 * paragraph's own leading put a blank line above and below a sentence
 * that belongs against the heading.
 */
function PhaseBanner(props: {
  phase: GameView['phase'] | undefined;
  waitingOnMe: boolean;
  shown: number;
  view: GameView;
  nameOf: (robotId: string) => string;
}) {
  const line = phaseLine(props);
  return line && <div className="phase-banner">{line}</div>;
}

function phaseLine({
  phase, waitingOnMe, shown, view, nameOf,
}: {
  phase: GameView['phase'] | undefined;
  waitingOnMe: boolean;
  shown: number;
  view: GameView;
  nameOf: (robotId: string) => string;
}) {
  if (!phase) return null;

  const others = (ids: string[]) => ids.filter((robotId) => robotId !== view.youAre);

  switch (phase.kind) {
    case 'awaitingProgram': {
      // Who has NOT programmed, which only the server knows: submissions
      // park in a table and `robotIds` still names everyone who was asked
      // until the last one lands. Falling back to `robotIds` keeps a
      // client talking to an older server counting the whole table
      // rather than counting nobody.
      const waiting = others(phase.waitingFor ?? phase.robotIds);

      // Said whether or not this player has programmed. It used to be
      // hidden while they still had cards to place -- on the grounds that
      // the registers and the button already said it was their move --
      // which withheld it at exactly the moment it decides something:
      // whether there is time to think, or whether the table is on you.
      //
      // NAMED, not counted. "Waiting for 2 other players" is the same
      // sentence whoever they are, and the answer a player wants is
      // whether it is the one who always takes a while or the one who
      // just said they were done.
      if (waiting.length > 0) {
        return (
          <p className="muted">
            Waiting for {waiting.map(nameOf).join(', ')}.
          </p>
        );
      }

      // Nobody else left to program. Either the turn is about to run, or
      // this player is the whole of what is holding it.
      if (!waitingOnMe) return <p className="muted">Resolving the turn.</p>;
      // Only worth saying where there is somebody to be held up. An
      // eliminated player is not coming back, so a solo game and a game
      // everyone else is out of are the same table of one.
      const otherPlayers = view.robots.filter(
        (r) => r.id !== view.youAre && !r.eliminated,
      ).length;
      return otherPlayers > 0
        ? <p className="notice warn">Everyone is waiting for you.</p>
        : null;
    }
    case 'awaitingPowerDown':
      return (
        <p className={waitingOnMe ? 'notice ok' : 'muted'}>
          {waitingOnMe ? 'Decide about powering down.' : 'Waiting on power-down decisions.'}
        </p>
      );
    case 'runningRegister':
    case 'awaitingRegisterChoices':
    // Mid-register prompts are still that register: without these the
    // heading fell through to `default` and went blank while the player
    // was being asked something.
    case 'awaitingOptionUse':
    case 'awaitingWeaponChoice':
    case 'awaitingAbortChoice':
      return <p className="eyebrow">Register {shown} of 5</p>;
    case 'awaitingEndOfTurn':
      return <p className="eyebrow">End of turn</p>;
    case 'deal':
      return <p className="eyebrow">Dealing</p>;
    default:
      return null;
  }
}

function EndOfTurn({
  phase, me, busy, isHost, optionsAvailable, nameOf, onSend,
}: {
  phase: Extract<GameView['phase'], { kind: 'awaitingEndOfTurn' }>;
  me: GameView['robots'][number] | null;
  busy: boolean;
  isHost: boolean;
  /** Whether a two-wrench site has a card to offer. With none left the
   * engine heals instead, so the choice is not put. */
  optionsAvailable: number;
  /** Names the robots still to answer, so the host is told WHO rather
   * than how many. */
  nameOf: (robotId: string) => string;
  onSend: (choices: unknown) => void;
}) {
  const [facing, setFacing] = useState<'N' | 'E' | 'S' | 'W'>('N');
  const [repair, setRepair] = useState<'heal' | 'option'>('heal');
  const [returnPoweredDown, setReturnPoweredDown] = useState(false);
  const [continuePoweredDown, setContinuePoweredDown] = useState(false);
  const needsFacing = me ? phase.facing.includes(me.id) : false;
  const needsRepair = me ? phase.repair.includes(me.id) : false;
  // A robot coming back from destruction chooses whether to return
  // powered down. The engine has always accepted this choice; nothing
  // ever offered it, so it was never made.
  const needsReturnChoice = me ? phase.returnPowerDown.includes(me.id) : false;
  // A robot that spent this turn powered down chooses whether to stay
  // down for the next one. Same shape as the choice above and the same
  // fault: the engine builds the list and accepts the answer, the server
  // forwards it, and nothing ever asked -- so `continuePowerDownChoices`
  // was always empty, the engine's `?? false` stood, and every
  // powered-down robot came back up whether its player wanted it to or
  // not.
  const needsContinueChoice = me ? phase.continuePowerDown.includes(me.id) : false;
  // "You lose one option if you have one. You get to pick which one."
  // Only asked when there is more than one to pick between.
  const needsDiscard = me ? (phase.discardOption ?? []).includes(me.id) : false;

  // The choice has to actually be MADE. Sending without it let the engine
  // fall back to the robot's first Option, so a player could end the turn
  // and silently lose something they had not picked. A robot holding
  // nothing is not asked, so there is nothing to wait for.
  const hasOptionsToGiveUp = (me?.options ?? []).length > 0;

  // Robots that still owe a choice. The host's own counts as sent the
  // moment this panel can submit it, since it travels with the advance
  // rather than being parked first.
  const stillDeciding = (phase.waitingFor ?? []).filter((id) => !(isHost && id === me?.id));
  // WHO, not how many: the button read "Waiting for 1", which named
  // neither the thing awaited nor the player who owed it. Two names fit on
  // a button; past that it is a number again and the tooltip has the list.
  const waitingOn = stillDeciding.length <= 2
    ? stillDeciding.map(nameOf).join(' and ')
    : `${stillDeciding.length} players`;
  const [discardId, setDiscardId] = useState<string | null>(null);
  const discardPending = needsDiscard && hasOptionsToGiveUp && !discardId;

  // Whether this player's choices are already parked on the server: their
  // robot is off the waiting list. A robot picking its facing could go on
  // clicking the buttons for ever with nothing to say the send had
  // worked. `justSent` covers the moment between the click and the view
  // coming back.
  //
  // Never the host: their choices travel with their own advance rather
  // than being parked first, so they are on the list until the turn ends.
  const [justSent, setJustSent] = useState(false);
  const owes = needsFacing || needsRepair || needsReturnChoice
    || needsContinueChoice || needsDiscard;
  const sent = !isHost && owes
    && (justSent || !(phase.waitingFor ?? []).includes(me?.id ?? ''));

  return (
    <div className="stack">
      {needsFacing && (
        <div>
          <label>Your robot is coming back. Which way should it face?</label>
          <div className="row">
            {(['N', 'E', 'S', 'W'] as const).map((d) => (
              <button
                key={d}
                className={facing === d ? '' : 'secondary'}
                onClick={() => setFacing(d)}
                disabled={sent}
              >
                {d}
              </button>
            ))}
          </div>
        </div>
      )}

      {needsDiscard && me && (
        <div className="stack" style={{ gap: '0.3rem' }}>
          <p className={discardPending ? 'notice warn' : 'notice'}>
            Your robot was destroyed. Choose an option to give up.
          </p>
          <div className="option-tray">
            {/* Keyed by POSITION as well as id: a hand that holds two of
                the same card would otherwise give React two chips with
                one key. */}
            {(me.options ?? []).map((option, i) => (
              <button
                key={`${option.id}-${i}`}
                className={
                  discardId === option.id ? 'option-chip chosen' : 'option-chip'
                }
                onClick={() => setDiscardId(option.id)}
                disabled={sent}
                title={option.text}
              >
                {option.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {needsReturnChoice && (
        <label className="powerdown">
          <input
            type="checkbox"
            checked={returnPoweredDown}
            onChange={(e) => setReturnPoweredDown(e.target.checked)}
            disabled={sent}
          />
          <span>
            Come back powered down
            <span className="muted"> - takes no actions next turn, and discards the 2 damage of returning</span>
          </span>
        </label>
      )}

      {needsContinueChoice && (
        <label className="powerdown">
          <input
            type="checkbox"
            checked={continuePoweredDown}
            onChange={(e) => setContinuePoweredDown(e.target.checked)}
            disabled={sent}
          />
          <span>
            Stay powered down
            <span className="muted"> - takes no actions next turn either, and discards all damage again</span>
          </span>
        </label>
      )}

      {/*
        The two-wrench site's real choice: heal 2 OR take an option, never
        both. This used to be a line of text saying Options were not in
        play and a hard-coded `heal`, so the choice the engine and the
        server had both always accepted was never once put to a player.
        With nothing left to draw it is not a choice -- the engine heals
        and says why -- so the buttons give way to the reason.
      */}
      {needsRepair && (
        <div>
          <label>You are on a two-wrench site.</label>
          {optionsAvailable > 0 ? (
            <div className="row">
              <button
                className={repair === 'heal' ? '' : 'secondary'}
                onClick={() => setRepair('heal')}
                disabled={sent}
                title="Repair 2 damage"
              >
                Repair 2
              </button>
              <button
                className={repair === 'option' ? '' : 'secondary'}
                onClick={() => setRepair('option')}
                disabled={sent}
                title="Take an option card instead, repairing nothing"
              >
                Take an option
              </button>
            </div>
          ) : (
            <p className="muted">
              Every option is already in play, so this repairs 2 damage.
            </p>
          )}
        </div>
      )}

      {!needsFacing && !needsRepair && !needsReturnChoice && !needsContinueChoice
        && !needsDiscard && !isHost && (
        <p className="muted">Waiting for the host to end the turn.</p>
      )}

      {(needsFacing || needsRepair || needsReturnChoice || needsContinueChoice
        || needsDiscard || isHost) && (
        <button
          onClick={() => {
            setJustSent(true);
            onSend({
              ...(needsFacing ? { facing } : {}),
              // `heal` whenever the choice was not offered, so a site with
              // nothing to draw is not reported as an option the player
              // asked for and did not get.
              ...(needsRepair
                ? { repair: optionsAvailable > 0 ? repair : 'heal' }
                : {}),
              ...(needsReturnChoice ? { returnPowerDown: returnPoweredDown } : {}),
              ...(needsContinueChoice ? { continuePowerDown: continuePoweredDown } : {}),
              ...(needsDiscard && discardId ? { discardOption: discardId } : {}),
            });
          }}
          // The host waits for everyone with something to decide. Ending
          // the turn early took the default for every unsubmitted choice:
          // a returning robot faced north, and one that meant to stay
          // powered down woke up.
          //
          // `sent` closes the panel down once the choice is parked: it is
          // the one thing on the screen that says the send worked.
          disabled={busy || sent || discardPending || (isHost && stillDeciding.length > 0)}
          title={
            sent
              ? 'Your choice is with the host'
              : discardPending
                ? 'Choose which option to give up first'
                : stillDeciding.length > 0
                  ? `Still to answer: ${stillDeciding.map(nameOf).join(', ')}`
                  : undefined
          }
        >
          {/* Names the step it RESOLVES rather than what follows it: end
              of turn leads to the next deal, or to game over if somebody
              touched the last flag, and this button cannot tell which. */}
          {isHost
            ? (stillDeciding.length > 0
              ? `Waiting for ${waitingOn}`
              : 'Resolve end of turn')
            : sent ? 'Choice sent' : 'Send my choice'}
        </button>
      )}

      {!isHost && (needsFacing || needsRepair || needsReturnChoice
        || needsContinueChoice || needsDiscard) && (
        <p className={sent ? 'notice ok' : 'muted'}>
          {sent
            ? 'Your choice is in. The host ends the turn.'
            : 'The host ends the turn. Send your choice and it will be used.'}
        </p>
      )}
    </div>
  );
}

function Standings({
  view, colours, nameOf, shown, isHost, busy, onRemove,
}: {
  view: GameView;
  colours: Map<string, string>;
  nameOf: (robotId: string) => string;
  /** The register the board is showing, for highlighting each strip. */
  shown: number;
  isHost: boolean;
  busy: boolean;
  /** Called with the ROBOT id; the caller maps it to the user id, which
   * is what the endpoint takes. */
  onRemove: (robotId: string) => void;
}) {
  return (
    <div className="stack" style={{ gap: '0.4rem' }}>
      <p className="eyebrow">Robots</p>
      {/* The viewer's own robot first: this is the row they look at every
          turn, and hunting for it in seating order is the one thing this
          list should never make them do. A stable sort, so everybody else
          keeps the turn order the view sent. */}
      {[...view.robots]
        .sort((a, b) => (
          Number(b.id === view.youAre) - Number(a.id === view.youAre)
        ))
        .map((robot) => (
        <div key={robot.id} className="stack" style={{ gap: '0.15rem' }}>
        <div className="standing">
          <span className="swatch" style={{ background: colours.get(robot.id) }} />
          {/* The same treatment for every name: the viewer's own row used
              to take the `display` class, which uppercases, so one name
              read as ROBOT1 and the rest as robot2. It is marked with
              weight instead. */}
          <span style={{ fontWeight: robot.id === view.youAre ? 600 : 400 }}>
            {nameOf(robot.id)}
          </span>
          <RobotStats robot={robot} />
        </div>

        {/* Everyone can see everyone's Options, and everyone's registers
            as they are revealed.

            The view has always sent both -- `redactRobot` passes `options`
            through untouched for every robot and fills `registers` in as
            far as `revealedThrough` allows -- and the roster showed only a
            COUNT of options and no registers at all. So the redaction was
            doing its job and nothing was drawing the result.

            Every robot is followed by what it holds, the viewer's
            included: the tray used to sit on its own above this list,
            which put a player's Options nowhere near the player. */}
        {/* The host can remove a player who has dropped. Not offered for
            the host's own row: there is no way to transfer the role, so a
            game whose host has left cannot be advanced at all -- the
            server refuses it too. */}
        {isHost && robot.id !== view.youAre && !robot.eliminated && (
          <button
            className="secondary"
            style={{ fontSize: '0.7rem', padding: '0.1rem 0.4rem' }}
            disabled={busy}
            title={'Removes the player. Their robot is eliminated, as if it '
              + 'had spent its last life.'}
            onClick={() => onRemove(robot.id)}
          >
            Remove
          </button>
        )}

        <div className="stack" style={{ gap: '0.15rem', marginBottom: '0.35rem' }}>
          {/* Not for the viewer, who has their own strip above. Drawing a
              second one is the duplicate-strip bug of 2026-08-19 over
              again. */}
          {robot.id !== view.youAre && (
            <RegisterStrip
              registers={robot.registers}
              current={shown}
              options={robot.options ?? []}
              locked={robot.lockedRegisters}
            />
          )}
          {/* THE SAME TRAY for every robot, the viewer's included, so an
              opponent's card can be opened and read like your own. It
              used to be a look-alike row of chips carrying the text in a
              `title` and nothing else -- which put the wording you most
              need, on a card you have never held, in the one copy you
              could not open. */}
          <OptionTray
            options={robot.options ?? []}
            activated={shown > 0 ? (robot.activatedOptions ?? []) : []}
            aimed={shown > 0 ? (robot.aimedOptions ?? []) : undefined}
          />
        </div>
        </div>
        ))}
    </div>
  );
}
